import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, Users, AlertCircle, CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { Helmet } from "react-helmet-async";
import { PackageAPI, PackageUI } from "@/api/type";
import { getPackageDetail } from "@/api/package";
import ProfitCalculator from "@/components/Plans/profitCaculator";


const PlanDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [plan, setPlan] = useState<PackageUI>();
  const [revenue, setRevenue] = useState(100);
  const [planAPI, setPlanAPI] = useState<PackageAPI | null>(null);

  useEffect(() => {
    if (!id) return;
    getPackageDetail(Number(id)).then((data) => {
      setPlanAPI(data);
      const mappedPlan: PackageUI = {
        id: data.id,
        name: data.name,
        description: data.description,
        type: 'fixed',
        status: data.is_active ? 'open' : 'closed',
        image: data.image || null,
        minInvestment: Number(data.price),
        maxInvestment: Number(data.price),
        profitRate: Number(data.min_profit_rate),
        profitPeriod: 'month',
        duration: data.duration_months,
        weight: data.part_per_user,
        total_weight: 210,
        maxSlot: data.total_slots,
        usedSlot: data.used_slots || 0,
        minPayment: data.min_profit_rate ? (Number(data.min_profit_rate) / 100) * Number(data.price) : undefined,
        commissionF1: Number(data.commission_rate),
        paymentCycle: 'monthly',
      };
      setPlan(mappedPlan);
    });
  }, [id]);

  const investmentAmount = plan?.minInvestment || 13000000;
  const remainingSlots = 24;
  const totalSlots = planAPI?.total_slots || 30;
  const slotPercentage = plan && plan.maxSlot && plan.usedSlot ? (plan.usedSlot / plan.maxSlot) * 100 : 0;

  const calculateProfit = (revenueInBillion: number) => {
    const sharePercentage = 8;
    const totalShares = 210;
    const myShares = 1;
    const minGuarantee = 45;

    const sharePool = revenueInBillion * sharePercentage / 100;
    const perShare = (sharePool * 1000) / totalShares;
    const myProfit = perShare * myShares;
    return Math.max(myProfit, minGuarantee);
  };

  const currentProfit = calculateProfit(revenue);
  const yearlyProfit = currentProfit * 12;
  const totalProfit = currentProfit * (plan?.duration || 36);

  const projectPhases = [
    {
      title: "Giai đoạn 1",
      duration: "Tháng 1-12",
      description: "Xây dựng nền tảng - Phát triển hệ thống quản lý học viện, tuyển dụng 50 giảng viên chất lượng cao, mở 10 trung tâm đầu tiên, đạo tạo 5,000 học viên"
    },
    {
      title: "Giai đoạn 2",
      duration: "Tháng 13-24",
      description: "Mở rộng quy mô - Tăng lên 30 trung tâm toàn quốc, đạo tạo 20,000 học viên, phát triển 20 khóa học mới, partnership với 100 doanh nghiệp để giới thiệu việc làm"
    },
    {
      title: "Giai đoạn 3",
      duration: "Tháng 25-36",
      description: "Tối ưu và Scale - Đạt 50 trung tâm, 50,000 học viên tích lũy, launch nền tảng học online, mở rộng ra thị trường khu vực, chuẩn bị IPO"
    }
  ];

  const additionalBenefits = [
    "Bảo cáo doanh thu chi tiết hàng tháng với breakdown theo từng trung tâm",
    "Dashboard theo dõi realtime số lượng học viên, doanh thu, lợi nhuận",
    "Quyền tham dự Đại hội đối tác hàng quý tại khách sạn 5 sao",
    "Ưu tiên cho người thân tham gia các khóa học với giảm giá 50%",
    "Cơ hội tham gia các dự án mở rộng mới với điều kiện ưu đãi",
  ];

  const handleContinue = () => {
    navigate(`/plans/${id}/contract`);
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Dashboard", path: "/home" },
    { label: "Gói đầu tư", path: "/plans" },
    { label: plan?.name || 'Loading...' },
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN').format(value);
  };

  if (!plan) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="flex items-center justify-center">
          <img src="/loading.svg" alt="Loading" />
        </div>
        <p className="!text-gray-500 dark:!text-dark-text mt-4">Đang tải chi tiết gói...</p>
      </div>
    );
  }

  return (
    <div className="dark:!bg-dark-bg">
      <Helmet>
        <title>{plan.name} - BeQs Reward</title>
      </Helmet>
      <PageHeader title="Chi tiết gói" breadcrumbs={breadcrumbs} />

      <div className="!mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Package detail */}
          <div className="lg:col-span-2 space-y-6">
            <div className="!bg-white dark:!bg-main-dark rounded-md overflow-hidden">
              <div className="relative h-64 lg:h-120 w-full">
                <img
                  src={plan.image || "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&h=400&fit=crop"}
                  alt={plan.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&h=400&fit=crop";
                  }}
                />
                <div className="absolute top-4 right-4">
                  <span className="px-4 py-2 bg-status-success text-white text-sm font-medium rounded-full">
                    {plan.name}
                  </span>
                </div>
              </div>

              <div className="rounded-md p-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-200 pb-2 mb-2 border-b border-border-primary">Thông tin chính</h1>
                <h2 className="!text-xl !font-semibold !text-gray-900 dark:!text-white mb-4">
                  {plan.name}
                </h2>
                <p className="text-sm !text-gray-700 dark:!text-gray-200 leading-relaxed">
                  {plan.description || 'BeQs Reward mang đến cơ hội đầu tư an toàn với lợi nhuận hấp dẫn qua gói đầu tư giáo dục chất lượng cao. Với cam kết lợi nhuận tối thiểu 6%/tháng và hoa hồng F1 lên đến 6%, bạn sẽ được hưởng thu nhập ổn định từ doanh thu ròng của hệ thống trung tâm đào tạo. Hãy cùng chúng tôi xây dựng tương lai giáo dục Việt Nam và tận hưởng lợi ích từ sự phát triển bền vững của ngành học tập suốt đời.'}
                </p>
              </div>
            </div>

            <ProfitCalculator default={plan.name} />

            {/* Project Overview */}
            {/* <div className="!bg-white dark:!bg-main-dark rounded-md p-6">
              <h3 className="!text-xl !font-semibold !text-gray-900 dark:!text-white mb-4">
                Quy mô dự án
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 dark:bg-dark-card-elevated rounded-md p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Tổng giá trị dự án</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">54 tỷ</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-card-elevated rounded-md p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Mục tiêu huy động</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">9 tỷ</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-card-elevated rounded-md p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Đã huy động</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">1.8 tỷ</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2">
                    <svg className="w-5 h-5 text-gray-800 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Địa điểm</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Toàn quốc</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2">
                    <svg className="w-5 h-5 text-gray-800 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Lĩnh vực</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Giáo dục & Đào tạo</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2">
                    <Users className="w-5 h-5 text-gray-800 dark:text-gray-200" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Số nhà đầu tư</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">6 người</p>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Project Phases */}
            <div className="!bg-white dark:!bg-main-dark rounded-md p-6">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-5 h-5 text-gray-600 dark:text-gray-100" />
                <h3 className="!text-xl !font-semibold !text-gray-900 dark:!text-white">
                  Nội dung và thời hạn hợp tác
                </h3>
              </div>

              <div className="space-y-4 mb-6">
                {projectPhases.map((phase, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="bg-primary dark:bg-status-info text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                        {index + 1}
                      </div>
                      {index < projectPhases.length - 1 && (
                        <div className="w-0.5 bg-gray-200 dark:bg-dark-border flex-1 mt-2"></div>
                      )}
                    </div>
                    <div className="flex-1 pb-6">
                      <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                        {phase.title}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-200 font-medium mb-2">{phase.duration}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-200 leading-relaxed">
                        {phase.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-6 border-t border-gray-200 dark:border-gray-400">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                  Quyền lợi nhà đầu tư
                </h4>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Các quyền lợi dưới đây không cấu thành cam kết lợi nhuận tài chính
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {additionalBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-status-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600 dark:text-gray-200">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right cta fixed */}
          <div className="lg:col-span-1">
            <div className="!bg-white dark:!bg-main-dark rounded-md p-6 sticky top-20">
              <div className="mb-6">
                <p className="!text-xs !text-gray-500 dark:!text-gray-200 mb-2">MỨC ĐẦU TƯ CỐ ĐỊNH</p>
                <div className="mb-1">
                  <span className="!text-3xl !font-bold !text-gray-900 dark:!text-white">{formatCurrency(investmentAmount)}</span>
                  <span className="text-2xl font-semibold text-gray-900 dark:text-white ml-1">đ</span>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-600">
                  <span className="!text-sm !text-gray-900 dark:!text-gray-200">Thu nhập/tháng</span>
                  <span className="!text-base !font-semibold !text-gray-900 dark:!text-white">{plan.minPayment ? formatCurrency(plan.minPayment) + ' đ' : 'Theo doanh thu'}</span>
                </div>

                <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-600">
                  <span className="!text-sm !text-gray-900 dark:!text-gray-200">Hoa hồng F1</span>
                  <span className="!text-base !font-semibold !text-gray-900 dark:!text-white">{plan.commissionF1}%</span>
                </div>

                <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-600">
                  <span className="!text-sm !text-gray-900 dark:!text-gray-200">Số phần nhận</span>
                  <span className="!text-base !font-semibold !text-gray-900 dark:!text-white">{plan.weight} / {plan.total_weight} phần</span>
                </div>
              </div>

              <div className="!bg-gray-50 dark:!bg-dark-card-elevated rounded-md p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 !text-status-success dark:!text-gray-400" />
                  <span className="!text-sm !font-medium !text-gray-700 dark:!text-gray-300">
                    Còn {(plan.maxSlot || 0) - (plan.usedSlot || 0)} vị trí
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-1">
                  <div
                    className="bg-status-success dark:bg-gray-400 h-2 rounded-full transition-all"
                    style={{ width: `${slotPercentage}%` }}
                  ></div>
                </div>
                <p className="!text-xs !text-gray-900 dark:!text-gray-300 text-right">
                  {slotPercentage.toFixed(0)}% đã được đặt
                </p>
              </div>

              <button
                onClick={handleContinue}
                className="w-full !px-6 !py-3 !bg-primary !text-white rounded-md hover:!bg-primary/90 !transition-colors !border-none !font-semibold"
              >
                Đầu tư ngay
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanDetail;
